"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function InteriorExteriorMastery() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    code: "SVC / 01",
    title: "Interior Painting",
    description: "Precision application for walls, trim, and ceilings, ensuring a flawless, durable finish.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/interior_painting_detail.png-wvc-sizes",
    features: ["Color consultation", "Surface preparation", "Premium low-VOC paints"]
  }, {
    code: "SVC / 02",
    title: "Exterior Painting",
    description: "Weather-resistant coatings designed to protect and elevate your home's curb appeal.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/exterior_home_facade.png-wvc-sizes",
    features: ["Weatherproofing", "Power washing", "Long-lasting finish"]
  }, {
    code: "SVC / 03",
    title: "Cabinet Refinishing",
    description: "Transform your kitchen with professional-grade cabinet painting and restoration services.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/kitchen_cabinet_refinish.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/kitchen_cabinet_refinish.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699978/img/kitchen_cabinet_refinish.png-wvc-sizes",
    features: ["Custom color matching", "Durable lacquer", "Hardware installation"]
  }];
  return <section data-section-id="818" id="interior-exterior-mastery" className="relative bg-background text-foreground py-20 md:py-32 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/60 mb-6">
              03 / Our Craft
            </div>
            <h2 className="font-default font-semibold tracking-tight text-foreground text-[2.5rem] sm:text-5xl lg:text-[4.5rem] leading-[0.95]">
              Precision strokes.
              <br />
              <span className="italic font-serif font-normal">Lasting impressions.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base lg:text-lg text-foreground/70 leading-relaxed mb-6">
              From delicate interior detailing to robust exterior protection — every Elite Brushstrokes project is executed with architectural precision and premium materials.
            </p>
            <Link to="/OurCraftPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-foreground border-b border-foreground pb-1 hover:gap-3 transition-all">
              View all services
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => <div data-index={i} key={i} className={`group border border-foreground/20 bg-card flex flex-col overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${i === 1 ? "md:translate-y-12" : ""}`} style={{
          aspectRatio: "auto"
        }}>
              {}
              <div className="relative w-full overflow-hidden aspect-[4/3]">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="flex-1 flex flex-col p-8">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/60">
                    {service.code}
                  </span>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-foreground/20 text-primary transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-45 group-hover:border-primary">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>

                <h3 className="font-default text-2xl md:text-[28px] font-semibold tracking-tight text-foreground leading-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-default text-sm text-foreground/70 leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="mt-auto flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 pt-6 border-t border-foreground/10">
                  {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-center gap-2 font-mono text-[11px] tracking-wide uppercase text-foreground/80">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg bg-primary" />
                      {feature}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-foreground/20">
          <p className="font-default text-sm text-foreground/70 max-w-md">
            Ready to transform your space? Request a professional consultation and receive a custom quote today.
          </p>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-lg font-mono text-xs tracking-[0.15em] uppercase">
            <Link to="/GetAQuotePage">
              Request a Free Quote
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}